import * as messages from '@/integrations/paraglide/messages/_index.js'
import { getLocale } from '@/integrations/paraglide/runtime'
import { normalizeEntityKey } from '@/lib/entity-key-normalization'

type MessageFn = () => string
const messageMap = messages as unknown as Record<string, MessageFn>

function readMessage(key: string): string | undefined {
	if (!Object.hasOwn(messageMap, key)) {
		return undefined
	}

	const fn = messageMap[key]
	if (typeof fn === 'function') {
		return fn()
	}
	return undefined
}

function keyify(value: string): string {
	return value
		.replaceAll(/([a-z])([A-Z])/g, '$1_$2')
		.replaceAll(/[.\-\s/]+/g, '_')
		.replaceAll(/__+/g, '_')
		.replaceAll(/(?:^_+|_+$)/g, '')
		.toLowerCase()
}

function camelize(value: string): string {
	const words = value
		.replaceAll(/([a-z])([A-Z])/g, '$1 $2')
		.replaceAll(/[._\-/]+/g, ' ')
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((word) => word.toLowerCase())

	if (!words.length) {
		return ''
	}

	return (
		words[0] +
		words
			.slice(1)
			.map((word) => `${word[0]?.toUpperCase() ?? ''}${word.slice(1)}`)
			.join('')
	)
}

function unique(values: string[]): string[] {
	return Array.from(new Set(values.filter(Boolean)))
}

function suffixCandidates(value: string): string[] {
	const compact = value.replaceAll(/[-_\s]/g, '').toLowerCase()
	return unique([
		value,
		keyify(value),
		camelize(value),
		compact,
		normalizeEntityKey(value),
	])
}

function resolveByPrefixes(
	value: string,
	prefixes: string[],
): string | undefined {
	const suffixes = suffixCandidates(value)
	for (const prefix of prefixes) {
		for (const suffix of suffixes) {
			const localized = readMessage(`${prefix}${suffix}`)
			if (localized) {
				return localized
			}
		}
	}

	return undefined
}

function formatFallbackLabel(value: string) {
	if (!value) {
		return ''
	}

	return value
		.replaceAll(/([a-z])([A-Z])/g, '$1 $2')
		.replaceAll(/[-_]+/g, ' ')
		.replaceAll(/\s+/g, ' ')
		.trim()
		.replaceAll(/^./g, (char) => char.toUpperCase())
}

function readStringField(
	record: Record<string, unknown>,
	field: string,
): string | undefined {
	const value = record[field]
	if (typeof value !== 'string') {
		return undefined
	}

	const trimmed = value.trim()
	return trimmed.length ? trimmed : undefined
}

export function getEntityRecordLabel(
	entity: string,
	record: unknown,
	fallback?: string,
): string {
	if (!record || typeof record !== 'object' || Array.isArray(record)) {
		return fallback ?? ''
	}

	const row = record as Record<string, unknown>

	if (entity === 'users') {
		const firstName = readStringField(row, 'firstName')
		const lastName = readStringField(row, 'lastName')
		const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()
		if (fullName) {
			return fullName
		}
	}

	if (entity === 'custompages') {
		const displayName =
			readStringField(row, 'displayName') ??
			readStringField(row, 'customPageDisplayName')
		if (displayName) {
			return displayName
		}
	}

	return (
		readStringField(row, 'name') ??
		readStringField(row, 'displayName') ??
		readStringField(row, 'title') ??
		fallback ??
		''
	)
}

export function getEntityLabel(entity: string) {
	const resolved = resolveByPrefixes(entity, ['entity_'])
	if (resolved) {
		return resolved
	}

	switch (normalizeEntityKey(entity)) {
		case 'user':
			return messages.entity_user()
		case 'team':
			return messages.entity_team()
		case 'queue':
			return messages.entity_queue()
		case 'role':
			return messages.entity_role()
		case 'skill':
			return messages.entity_skill()
		case 'skillgroup':
			return messages.entity_skillgroup()
		case 'template':
			return messages.entity_template()
		case 'pausereason':
			return messages.entity_pausereason()
		case 'customproperty':
			return messages.entity_customproperty()
		case 'custompage':
			return messages.entity_custompage()
		case 'ruleset':
			return messages.entity_ruleset()
		default:
			return formatFallbackLabel(entity)
	}
}

export function getEntityTabLabel(tab: string) {
	const resolved = resolveByPrefixes(tab, ['tab_', 'nav_'])
	if (resolved) {
		return resolved
	}

	switch (normalizeEntityKey(tab)) {
		case 'overview':
		case 'detail':
			return messages.tab_detail()
		case 'user':
			return messages.nav_users()
		case 'team':
			return messages.nav_teams()
		case 'role':
			return messages.nav_roles()
		case 'skill':
			return messages.nav_skills()
		case 'skillgroup':
			return messages.nav_skillGroups()
		case 'queue':
			return messages.nav_queues()
		case 'custompage':
			return messages.nav_customPages()
		case 'template':
			return messages.nav_templates()
		case 'pausereason':
			return messages.nav_pauseReasons()
		case 'settings':
			return messages.nav_settings()
		case 'permission':
			return messages.entity_permissions()
		case 'customproperty':
			return messages.nav_customProperties()
		case 'ruleset':
			return messages.entity_ruleSets()
		case 'contactcode':
			return messages.tab_contactCodeTree()
		case 'entityfilter':
			return messages.tab_entityFilter()
		default:
			return formatFallbackLabel(tab)
	}
}

export function getActionEntityLabel(
	actionLabel: string,
	entity: string,
): string {
	const localizedEntity = getEntityLabel(entity)
	const normalizedAction = actionLabel.trim()

	if (!normalizedAction) {
		return localizedEntity
	}

	if (getLocale() === 'de') {
		return `${localizedEntity} ${normalizedAction.toLowerCase()}`
	}

	return `${normalizedAction} ${localizedEntity}`
}
